/**
 * LiveKit API base URL and endpoints. Update API_BASE_URL (or set
 * REACT_APP_TOKEN_API_URL) when the server URL changes.
 */
const API_BASE_URL = (process.env.REACT_APP_TOKEN_API_URL || "http://localhost:8080/livekit").replace(
  /\/$/,
  ""
);

/**
 * VSS server root WITHOUT the `/livekit` suffix — used by the socket.io signalling
 * connection and the recording-download redirect (src/hooks/useWebRTC.js). Derived from
 * the same env (`REACT_APP_TOKEN_API_URL`) as the LiveKit API base so it tracks per
 * environment instead of a hardcoded host.
 */
export const SERVER_BASE_URL = API_BASE_URL.replace(/\/livekit$/, "");

function resolveLiveKitWsUrl() {
  const configured = process.env.REACT_APP_LIVEKIT_WS_URL;
  if (configured && configured.trim()) {
    if (typeof window !== "undefined" && window.location.protocol === "https:") {
      return configured.replace(/^ws:\/\//i, "wss://");
    }
    return configured;
  }

  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    // Safe production fallback when app itself is served over HTTPS.
    return `wss://${window.location.hostname}:7880`;
  }

  return "ws://18.215.237.215:7880";
}

export const LIVEKIT_WS_URL = resolveLiveKitWsUrl();

/**
 * Bot-backend base URL for local observer testing only (no `/observer` suffix).
 * Example: `REACT_APP_OBSERVER_API_URL=http://localhost:8000`
 * Leave unset in production; start observers from a webhook or backend job instead.
 */
export const OBSERVER_API_BASE_URL = (process.env.REACT_APP_OBSERVER_API_URL || "").replace(
  /\/$/,
  ""
);

function envTruthy(v) {
  const s = String(v ?? "").trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes";
}

/**
 * user-management base (public, unauthenticated config reads only). Set
 * `REACT_APP_UM_API_URL` to the UM host WITHOUT `/api` (e.g.
 * https://user-management.theinterviews.ai). TI-1111: the consent modal derives its
 * audio-transcription clause from `GET /api/config/observer-lifelike` — the same server
 * flag that gates the bot-backend observer — so the disclosure cannot drift from what
 * the server actually transcribes. Unset ⇒ the disclosure fails closed to shown
 * (see src/livekit/observerDisclosure.js).
 */
export const UM_API_BASE_URL = (process.env.REACT_APP_UM_API_URL || "").replace(/\/$/, "");

export const OBSERVER_LIFELIKE_CONFIG_URL = UM_API_BASE_URL
  ? `${UM_API_BASE_URL}/api/config/observer-lifelike`
  : "";

/**
 * Gate `LocalObserverBootstrap` (browser → bot-backend POST /observer/...).
 * Set `REACT_APP_OBSERVER_ENABLED=true` and `REACT_APP_OBSERVER_API_URL` (e.g. http://localhost:8000).
 * If the flag is false/unset or the URL is empty, observer calls are skipped.
 */
export const OBSERVER_UI_ENABLED = envTruthy(process.env.REACT_APP_OBSERVER_ENABLED);

/**
 * integrity-service base (face/identity verification + edge signals, TI-267 E4).
 * Separate host from VSS. Set `REACT_APP_INTEGRITY_API_URL` (e.g. http://localhost:8000).
 * Gated by `REACT_APP_INTEGRITY_ENABLED`; if the flag is off or the URL empty the
 * monitor never runs (the room is unaffected). The parent app (interviews-ui) only
 * embeds with integrity on when the platform flag is enabled for the customer.
 */
export const INTEGRITY_API_BASE_URL = (process.env.REACT_APP_INTEGRITY_API_URL || "").replace(
  /\/$/,
  ""
);

export const INTEGRITY_UI_ENABLED =
  envTruthy(process.env.REACT_APP_INTEGRITY_ENABLED) && !!INTEGRITY_API_BASE_URL;

export const INTEGRITY_ENDPOINTS = {
  START: `${INTEGRITY_API_BASE_URL}/v1/identity/start`,
  VERIFY_FRAME: `${INTEGRITY_API_BASE_URL}/v1/identity/verify-frame`,
  TERMINATE: `${INTEGRITY_API_BASE_URL}/v1/identity/terminate`,
  CONSENT: `${INTEGRITY_API_BASE_URL}/v1/identity/consent`,
  EDGE_SIGNALS: `${INTEGRITY_API_BASE_URL}/v1/identity/edge-signals`,
};

export const ENDPOINTS = {
  TOKEN: `${API_BASE_URL}/token`,
  RECORD_CONFIG: `${API_BASE_URL}/record/config`,
  RECORD_STATUS: `${API_BASE_URL}/record/status`,
  RECORD_START: `${API_BASE_URL}/record/start`,
  RECORD_STOP: `${API_BASE_URL}/record/stop`,
  RECORD_STOP_APPROVE: `${API_BASE_URL}/record/stop/approve`,
  /** Mic/cam/screenshare while recording → Redis timeline (Bearer telemetry JWT required). */
  RECORD_TIMELINE_CLIENT: `${API_BASE_URL}/record/timeline/client`,
  /** Exchange LiveKit JWT for short-lived telemetry JWT (timeline + client logs). */
  TELEMETRY_TOKEN: `${API_BASE_URL}/telemetry/token`,
  /** Client errors / diagnostics (Bearer telemetry JWT). */
  CLIENT_LOGS: `${API_BASE_URL}/client/logs`,
  /** One-shot join snapshot: mic/cam/screen/network (Bearer telemetry JWT). */
  CLIENT_SESSION: `${API_BASE_URL}/client/session`,
  /** Tab visibility / focus / network quality (Bearer telemetry JWT). */
  CLIENT_PRESENCE: `${API_BASE_URL}/client/presence`,
};

// Log at load so you can confirm which endpoint the app is using (main app = src/livekit)
if (typeof window !== "undefined") {
  console.log("[LiveKit] ENDPOINTS.TOKEN", ENDPOINTS.TOKEN);
  console.log("[LiveKit] ENDPOINTS.TELEMETRY_TOKEN", ENDPOINTS.TELEMETRY_TOKEN);
  console.log("[LiveKit] ENDPOINTS.RECORD_TIMELINE_CLIENT", ENDPOINTS.RECORD_TIMELINE_CLIENT);
  console.log("[LiveKit] ENDPOINTS.CLIENT_LOGS", ENDPOINTS.CLIENT_LOGS);
  console.log("[LiveKit] ENDPOINTS.CLIENT_SESSION", ENDPOINTS.CLIENT_SESSION);
  console.log("[LiveKit] ENDPOINTS.CLIENT_PRESENCE", ENDPOINTS.CLIENT_PRESENCE);
  console.log("[LiveKit] LIVEKIT_WS_URL", LIVEKIT_WS_URL);
  console.log("[LiveKit] OBSERVER_UI_ENABLED", OBSERVER_UI_ENABLED);
  console.log("[LiveKit] OBSERVER_API_BASE_URL", OBSERVER_API_BASE_URL || "(unset)");
  if (OBSERVER_UI_ENABLED && !OBSERVER_API_BASE_URL) {
    console.warn("[LiveKit] OBSERVER_UI_ENABLED but OBSERVER_API_BASE_URL is unset — observer calls disabled");
  }
}
